import Legend from 'components/Legend';
import LoadingSpinner from 'components/LoadingSpinner';
import MobileNav from 'components/mobile/MobileNav';
import PrintModal from 'components/modal/PrintModal';
import Nav from 'components/Nav';
import SiteMapBuildingCanvas from 'components/SiteMapBuildingCanvas';
import SiteMapCanvas from 'components/SiteMapCanvas';
import ZoomOverlay from 'components/ZoomOverlay';
import {
  setFilterDictionary,
  setFilteredLots,
  // setSelectedBuildingId,
  // setCurrSelectedLevel,
  setSelectedFilters,
  setTotalSelectedFilters,
} from 'features/siteMapSlice';
import config from 'lib/config';
import gaFunction from 'lib/ga';
import { useSiteMapQuery } from 'lib/hooks/queries';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import useWindowDimensions from 'lib/hooks/useWindowDimensions';
import { ISelectedFilters } from 'lib/models/siteMap.interface';
import { getFilterDictionary, initiateSVG, showAndGetFilteredLots } from 'lib/utils';
import { useEffect, useState } from 'react';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import styled from 'styled-components';

const SiteMapPage = () => {
  // Redux Hooks and States
  const dispatch = useAppDispatch();
  const {
    print,
    showDetails,
    showDimensions,
    // selectedBuildingId,
  } = useAppSelector((state) => ({
    print: state.siteMap.print,
    showDetails: state.siteMap.showDetails,
    showDimensions: state.siteMap.showDimensions,
    // selectedBuildingId: state.siteMap.selectedBuildingId,
  }));

  // Get Site Map Data from React-Query Cache (No Params)
  const siteMapData = useSiteMapQuery().data!;

  // Semantic Vars From Site Map Data
  const {
    client: { sysId: clientSysId, name: clientName, clientDivision },
    community: { applicationID: communityId, name: communityName },
    displayOptions: [displayOptions],
    sessionData,
  } = siteMapData;

  // Constants
  const selectedBuildingId = '0';
  const URLParams = new URLSearchParams(window.location.search);
  const partnerToken = URLParams.get('partnerToken');
  const partnerHidePrint = URLParams.get('hidePrint') === 'true' ? true : false;
  const filterPlanSysId = URLParams.get('filterPlanSysId');
  const isIframe = window.location !== window.parent.location;

  // React Hooks
  const [isInitiated, setIsInitiated] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showAxis, setShowAxis] = useState(false);
  const [iframeZoomEnabled, setIframeZoomEnabled] = useState(false);
  const [zoomPanState, setZoomPanState] = useState({
    positionX: displayOptions.siteMapStartingX,
    positionY: displayOptions.siteMapStartingY,
    scale: displayOptions.siteMapStartingZoom,
  });
  const { isMobile } = useWindowDimensions();

  // Initialize Redux States
  useEffect(() => {
    if (isInitiated) {
      const filterDictionary = getFilterDictionary(siteMapData);
      dispatch(setFilterDictionary(filterDictionary));

      let selectedFilters: ISelectedFilters = {};
      Object.keys(filterDictionary).forEach((filterName) => (selectedFilters[filterName] = []));

      // hard filter by plan code
      if (filterPlanSysId) {
        selectedFilters.planName = [filterPlanSysId];
        // get associated lots and combine them
        const filteredLots = showAndGetFilteredLots({
          filterDictionary,
          selectedFilters,
          siteMapData,
        });
        dispatch(setFilteredLots(filteredLots));
        dispatch(setTotalSelectedFilters(1));
      }

      dispatch(setSelectedFilters(selectedFilters));
    }
  }, [dispatch, filterPlanSysId, siteMapData, isInitiated]);

  // Google Analytics Initialization
  useEffect(() => {
    gaFunction.initBasicTracking('web', 'sp');
    gaFunction.setClientInformation(clientSysId, clientName, clientDivision);
    gaFunction.setCommunityInformation(communityId, communityName);
    gaFunction.startSession();
    // eslint-disable-next-line
  }, []);

  // Scroll Handler in Iframe
  useEffect(() => {
    if (isIframe) {
      window.addEventListener('wheel', (e) => {
        if (!e.ctrlKey && !showOverlay) {
          setIframeZoomEnabled(false);
          setShowOverlay(true);
          setTimeout(() => setShowOverlay(false), 500);
        }
        if (e.ctrlKey) {
          setShowOverlay(false);
          setIframeZoomEnabled(true);
        }
      });
    }
  }, [isIframe, showOverlay]);

  // Show zoomPanState on ` press
  useEffect(() => {
    window.addEventListener('keydown', (e) => {
      if (e.key === '`') {
        setShowAxis((prev) => !prev);
      }
    });
  }, []);

  // Partner Integration
  useEffect(() => {
    if (sessionData?.lotInfo) {
      window.parent.postMessage(
        {
          lotInfo: sessionData?.lotInfo,
          partnerToken,
        },
        '*'
      );
    }
  }, [partnerToken, sessionData]);

  // Console Log Version for User Feedback and Troubleshooting
  useEffect(() => {
    console.log(`Next Site Map Version: "${config.version}"`);
  }, []);

  // Functions
  const onLoadFinish = () => {
    initiateSVG({
      siteMapData,
      showDetails,
      showDimensions,
      dispatch,
      gaFunction,
      setIsInitiated,
    });
  };

  const handleZoomStateChange = (ref: ReactZoomPanPinchRef) => {
    const { positionX, positionY, scale } = ref.state;
    setZoomPanState({
      positionX,
      positionY,
      scale,
    });
  };

  const onBack = (resetTransform: any) => {
    // dispatch(setSelectedBuildingId('0'));
    // dispatch(setCurrSelectedLevel(0));
    resetTransform(0);
  };

  return (
    <StyledSiteMapContainer
      data-cy="site-map-container"
      isTopLegend={displayOptions.legendPosition === 'top'}
    >
      {!isInitiated && <LoadingSpinner />}
      {!displayOptions.hideLegend && <Legend isMobileView={isMobile} />}
      {isMobile ? (
        <>
          <MobileNav />
          <SiteMapCanvas onLoadFinish={onLoadFinish} />
        </>
      ) : (
        <>
          <TransformWrapper
            initialScale={displayOptions.siteMapStartingZoom || undefined}
            initialPositionX={displayOptions.siteMapStartingX || undefined}
            initialPositionY={displayOptions.siteMapStartingY || undefined}
            maxScale={displayOptions.siteMapMaxZoom || undefined}
            wheel={{
              wheelDisabled: isIframe ? !iframeZoomEnabled : false,
            }}
            onZoom={handleZoomStateChange}
            onPanning={handleZoomStateChange}
          >
            {selectedBuildingId === '0'
              ? ({ resetTransform }) => (
                  <>
                    <Nav resetTransform={resetTransform} partnerHidePrint={partnerHidePrint} />
                    {showOverlay && <ZoomOverlay />}
                    <TransformComponent>
                      <SiteMapCanvas onLoadFinish={onLoadFinish} />
                    </TransformComponent>
                  </>
                )
              : ({ resetTransform }) => (
                  <>
                    <StyledButton onClick={() => onBack(resetTransform)}>Back</StyledButton>
                    {showOverlay && <ZoomOverlay />}
                    <TransformComponent>
                      <SiteMapBuildingCanvas
                        onLoadFinish={onLoadFinish}
                        resetTransform={resetTransform}
                      />
                    </TransformComponent>
                    {/* <SelectLevel /> */}
                  </>
                )}
          </TransformWrapper>
          {print && <PrintModal />}
        </>
      )}
      {zoomPanState && showAxis && (
        <div className="axis-zoom-display">
          <label>
            Zoom: <span>{zoomPanState.scale}</span>
          </label>
          <label>
            X: <span>{zoomPanState.positionX}</span>
          </label>
          <label>
            Y: <span>{zoomPanState.positionY}</span>
          </label>
        </div>
      )}
    </StyledSiteMapContainer>
  );
};

export default SiteMapPage;

const StyledSiteMapContainer = styled.div<{ isTopLegend: boolean }>`
  height: 100vh;
  width: 100vw;
  position: relative;
  background: ${({ theme }) => theme.colors.bgColor};
  .axis-zoom-display {
    position: absolute;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    padding: 10px;
    span {
      font-weight: 600;
      font-size: 18px;
      color: ${({ theme }) => theme.colors.primaryColor};
      cursor: text;
    }
  }
`;

const StyledButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primaryColor};
  color: ${({ theme }) => theme.colors.textColor};
  transition: all 0.3s ease 0s;
  border: none;
  font-weight: bold;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 6vw;
  height: 5vh;
  font-size: 1.4vw;
  position: absolute;
  margin-left: 3vw;
  margin-top: 60px;
  z-index: 10;

  &:disabled {
    cursor: not-allowed;
  }
`;
